import React, { createContext, useState, useEffect, useContext } from "react";

import { PostgrestSingleResponse, SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../supabase/database.types";
import { UserProfileDbType, BillingDbType } from "../supabase/types";
import { useSupabase } from "../supabase/supabase";
import { retrySupabaseOnFail } from "../utils/retry-supabase";

type UserProfileContextType = {
  userProfile:
    | (UserProfileDbType & {
        Billing: BillingDbType;
      })
    | null;
  accessToken: string | null;
  isLoading: boolean;
};

let tokenTimestamp: number = new Date().getTime();
export const getAccessToken = async (supabase: SupabaseClient<Database>) => {
  // if token timestamp is older than 5 minutes, refresh it
  if (new Date().getTime() - tokenTimestamp > 1000 * 60 * 5) {
    tokenTimestamp = new Date().getTime();
    await retrySupabaseOnFail(() => supabase.auth.refreshSession());
    const token = (await retrySupabaseOnFail(() => supabase.auth.getSession())).data.session?.access_token;

    return token as string;
  }
  const token = (await retrySupabaseOnFail(() => supabase.auth.getSession())).data.session?.access_token;
  return token as string;
};

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userProfile, setUserProfile] = useState<
    (UserProfileDbType & { Billing: BillingDbType }) | null
  >(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);
  const supabase = useSupabase();

  // Sync token to main process whenever it changes
  useEffect(() => {
    // Web app - no electron API
    // if (window.electronAPI?.updateAuthToken) {
    //   window.electronAPI.updateAuthToken(accessToken).then(() => {
    //     console.log('[UserProfileProvider] Auth token synced to main process successfully');
    //   }).catch((error) => {
    //     console.error('[UserProfileProvider] Failed to sync auth token:', error);
    //   });
    // }
  }, [accessToken]);

  // Function to refresh the token
  const refreshToken = async () => {
    await retrySupabaseOnFail(() => supabase.auth.refreshSession());
    const { data } = await retrySupabaseOnFail(() => supabase.auth.getSession());
    const token = data.session?.access_token;
    if (token) {
      setAccessToken(token);
      tokenTimestamp = new Date().getTime();
    } else {
      console.warn("Failed to refresh token: No access token returned");
    }
  };

  // Set up automatic token refresh every 4 minutes and load user profile
  useEffect(() => {
    // Skip if already initialized and we have both profile and token
    if (hasInitialized && userProfile && accessToken) {
      return;
    }

    const fetchInitialToken = async () => {
      const { data } = await retrySupabaseOnFail(() => supabase.auth.getSession());
      const token = data.session?.access_token;
      return token;
    };

    const fetchUserProfile = async () => {
      const {
        data: { user },
      } = await retrySupabaseOnFail(() => supabase.auth.getUser());

      if (!user?.id) return undefined;
      return await retrySupabaseOnFail(() => 
        supabase
          .from("UserProfile")
          .select("*, Billing(*)")
          .eq("id", user?.id as string)
          .single()
      );
    };

    Promise.all([fetchInitialToken(), fetchUserProfile()])
      .then(([token, profileResponse]) => {
        if (token) {
          setAccessToken(token);
        } else {
          // No token means no session, clear everything
          setAccessToken(null);
          setUserProfile(null);
        }

        if (profileResponse?.data) {
          setUserProfile(
            profileResponse.data as UserProfileDbType & {
              Billing: BillingDbType;
            }
          );
        } else if (token) {
          // Have token but no profile - user might need to complete onboarding
          setUserProfile(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching initial data:", error);
        // On error, clear everything
        setAccessToken(null);
        setUserProfile(null);
      })
      .finally(() => {
        setIsLoading(false);
        setHasInitialized(true);
      });

    // Refresh token every 4 minutes
    const tokenRefreshInterval = setInterval(() => {
      refreshToken();
    }, 1000 * 60 * 60);

    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, [supabase.auth, shouldReload, hasInitialized, userProfile]);

  // Listen to auth state changes in a separate effect
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") {
        // Always update the access token on sign in
        if (session?.access_token) {
          setAccessToken(session.access_token);
          tokenTimestamp = new Date().getTime();
        }
        
        // If we don't have a user profile yet, trigger a reload
        if (!userProfile) {
          setShouldReload((prev) => !prev);
        }
      } else if (event === "SIGNED_OUT") {
        // Clear user data when signed out
        setUserProfile(null);
        setAccessToken(null);
      } else if (event === "TOKEN_REFRESHED" && session?.access_token) {
        // Update token when refreshed
        setAccessToken(session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase.auth, hasInitialized, userProfile]);

  // Create context value with userProfile and accessToken at the same level
  const contextValue: UserProfileContextType = {
    userProfile,
    accessToken,
    isLoading,
  };

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    console.error("useUserProfile must be used within a UserProfileProvider");
    // Return default values to avoid undefined errors
    return { userProfile: null, accessToken: null } as UserProfileContextType;
  }
  return context;
};
